import React, { useState, useEffect, useMemo } from 'react';
import {
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
  Popover,
  Paper,
  CircularProgress,
  Box,
  alpha,
} from '@mui/material';

import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  LayoutDashboard,
  Calendar,
  Settings,
  Users,
  BookOpen,
  Briefcase,
  ShoppingBag,
  MessageSquareText,
  FileText,
  Bell,
  Blocks,
  FileInput,
  Clock,
  PlusCircle,
  Phone,
  CreditCard,
  // TODO: Uncomment when backend is ready
  // DollarSign,
  // Wallet,
  // ArrowUpCircle,
  // ArrowDownCircle,
} from 'lucide-react';
import { LAYOUT } from '../../../constants/layout';
import { RootState } from '../../../store';
import { useAuth } from '../../../hooks';
import { AUTH_CONSTANTS } from '../../../config/constants';
import { isSupportedTimezone } from '../../../config/regions';
import SubMenu from './SubMenu';
import SnippetComponent from '../../../views/SnippetComponent';
import { useChatLogs } from '../../../hooks/chat/useChatLogs';
import { useChatBookingLogs } from '../../../hooks/chat/useChatBookingLogs';
import { formEndpoints } from '../../../services/api/endpoints/form.endpoint';
import { Form } from '../../../types/form';
import { selectPaymentSettings } from '../../../store/slices/paymentSlice';

// Enhanced MenuItem interface to support sub-items
interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  className?: string;
  subItems?: {
    text: string;
    icon?: React.ReactNode;
    path: string;
    className?: string;
  }[];
  isDynamic?: boolean;
}

interface MenuItemsProps {
  isDrawerOpen: boolean;
}

const MenuItems: React.FC<MenuItemsProps> = ({ isDrawerOpen }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { role } = useAuth();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const paymentSettings = useSelector(selectPaymentSettings);

  // State for popover menu when drawer is collapsed
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  // State for dynamic forms
  const [forms, setForms] = useState<Form[]>([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);

  const isBookingEnabled = selectedOrganization?.isBookingEnabled ?? false;

  // TODO: For testing - toggle this to true/false to test payment menu
  // Replace with: selectedOrganization?.isStripeEnabled ?? false once backend field is added
  //const isStripeEnabled = true; // TESTING: Set to true to show Payment menu
  const isStripeEnabled = selectedOrganization?.isStripeEnabled ?? false;
  const rag = useChatLogs();
  const booking = useChatBookingLogs();
  const unreadChatLogs = isBookingEnabled ? booking.unreadCount : rag.unreadCount;

  // Fetch forms when organization changes
  useEffect(() => {
    const fetchForms = async () => {
      if (!selectedOrganization?.id) {
        setForms([]);
        return;
      }

      setIsLoadingForms(true);
      try {
        const fetchedForms = await formEndpoints.listForms(selectedOrganization.id);
        setForms(fetchedForms);
      } catch (error) {
        console.error('Failed to fetch forms:', error);
        setForms([]);
      } finally {
        setIsLoadingForms(false);
      }
    };

    fetchForms();
  }, [selectedOrganization?.id]);

  // Build menu items with dynamic forms
  const menuItems: MenuItem[] = useMemo(() => {
    const baseItems: MenuItem[] = [
      {
        text: 'Dashboard',
        icon: <LayoutDashboard size={18} />,
        path: '/dashboard',
        className: 'dashboard',
      },
    ];

    const bookingItems: MenuItem[] = [
      {
        text: 'Bookings',
        icon: <Calendar size={18} />,
        path: '/dashboard/bookings',
        className: 'bookings-menu',
      },
      {
        text: 'Staff',
        icon: <Briefcase size={18} />,
        path: '/dashboard/resources',
        className: 'resources-menu',
      },
      {
        text: 'Customers',
        icon: <Users size={18} />,
        path: '/dashboard/customers',
        className: 'customers-menu',
      },
      {
        text: 'Services',
        icon: <ShoppingBag size={18} />,
        path: '/dashboard/services',
        className: 'services-menu',
      },
    ];

    // Build forms submenu items
    const formsSubItems = [
      {
        text: 'Create Form',
        icon: <PlusCircle size={14} />,
        path: '/dashboard/forms/create',
        className: 'forms-create-menu',
      },
      ...forms.map(form => ({
        text: form.formName,
        icon: <FileText size={14} />,
        path: `/dashboard/forms/${form.uuid || form.formId}`,
        className: 'form-item-menu',
      })),
    ];

    const additionalItems: MenuItem[] = [
      {
        text: 'Knowledge Base',
        icon: <BookOpen size={18} />,
        path: '/dashboard/knowledge-base',
        className: 'knowledge-base-menu',
      },
      {
        text: 'Logs',
        icon: <MessageSquareText size={18} />,
        path: '/dashboard/chat-logs',
        className: 'chat-logs-menu',
      },
      {
        text: 'Forms',
        icon: <FileInput size={18} />,
        path: '/dashboard/forms',
        className: 'forms-menu',
        subItems: formsSubItems,
        isDynamic: true,
      },
    ];

    // Add Phone menu item for admin users only (supported regions + booking enabled)
    const timezone = selectedOrganization?.company?.timezone || '';
    const isPhoneSupported = isSupportedTimezone(timezone);
    if (role !== AUTH_CONSTANTS.ROLES.USER && isPhoneSupported && isBookingEnabled) {
      additionalItems.push({
        text: 'Phone',
        icon: <Phone size={18} />,
        path: '/dashboard/phone',
        className: 'phone-menu',
      });
    }

    // Add Payment as top-level menu when Stripe is enabled and booking is enabled
    if (role !== AUTH_CONSTANTS.ROLES.USER && isStripeEnabled && isBookingEnabled) {
      const isStripeSetup =
        paymentSettings !== null && paymentSettings.onboardingStatus === 'COMPLETE';

      const paymentSubItems = [
        {
          text: 'Setup',
          icon: <Settings size={14} />,
          path: '/dashboard/payment/setup',
        },
      ];

      // Only show these items if Stripe is set up
      if (isStripeSetup) {
        paymentSubItems.push({
          text: 'Invoices',
          icon: <FileText size={14} />,
          path: '/dashboard/payment/invoices',
        });
        // TODO: Uncomment when backend is ready
        // paymentSubItems.push(
        //   {
        //     text: 'Wallet',
        //     icon: <Wallet size={14} />,
        //     path: '/dashboard/payment/wallet',
        //   },
        //   {
        //     text: 'Transactions',
        //     icon: <ArrowUpCircle size={14} />,
        //     path: '/dashboard/payment/transactions',
        //   },
        //   {
        //     text: 'Refunds',
        //     icon: <ArrowDownCircle size={14} />,
        //     path: '/dashboard/payment/refunds',
        //   }
        // );
      }

      additionalItems.push({
        text: 'Payment',
        icon: <CreditCard size={18} />,
        path: '/dashboard/payment',
        className: 'payment-menu',
        subItems: paymentSubItems,
      });
    }

    if (role !== AUTH_CONSTANTS.ROLES.USER) {
      const subMenuItems = [
        {
          text: 'Users',
          icon: <Users size={14} />,
          path: '/dashboard/settings/users',
        },
        {
          text: 'Chat',
          icon: <MessageSquareText size={14} />,
          path: '/dashboard/settings/chat-settings',
          className: 'chat-settings-menu',
        },
        {
          text: 'Integrations',
          icon: <Blocks size={14} />,
          path: '/dashboard/settings/integrations',
          className: 'snippet-menu',
        },
      ];

      if (isBookingEnabled) {
        subMenuItems.push(
          {
            text: 'Notifications',
            icon: <Bell size={14} />,
            path: '/dashboard/settings/notifications',
          },
          {
            text: 'Hours',
            icon: <Clock size={14} />,
            path: '/dashboard/settings/working-hours',
            className: 'working-hours-menu',
          }
        );
      }

      additionalItems.push({
        text: 'Settings',
        icon: <Settings size={18} />,
        path: '/dashboard/settings',
        subItems: subMenuItems,
        className: 'settings-menu',
      });
    }

    return isBookingEnabled
      ? [...baseItems, ...bookingItems, ...additionalItems]
      : [...baseItems, ...additionalItems];
  }, [isBookingEnabled, role, forms, paymentSettings, selectedOrganization, isStripeEnabled]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>, itemId: string) => {
    if (!isDrawerOpen && event.currentTarget) {
      setAnchorEl(event.currentTarget);
      setOpenPopoverId(itemId);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setOpenPopoverId(null);
  };

  return (
    <List sx={{ px: 0 }}>
      {selectedOrganization?.id && <SnippetComponent clientId={selectedOrganization.id} />}
      {menuItems.map(item => {
        const isSelected = location.pathname === item.path;
        const isSubItemSelected = item.subItems?.some(
          subItem => location.pathname === subItem.path
        );
        const isMenuActive = isSelected || isSubItemSelected;

        if (item.subItems && item.subItems.length > 0) {
          return isDrawerOpen ? (
            // When drawer is open, use SubMenu component
            <SubMenu
              key={item.text}
              item={item}
              isDrawerOpen={isDrawerOpen}
              isSelected={isSelected}
              isLoading={item.isDynamic && isLoadingForms}
            />
          ) : (
            // When drawer is collapsed, use popover for submenus
            <React.Fragment key={item.text}>
              <ListItem
                button
                onClick={e => handlePopoverOpen(e as React.MouseEvent<HTMLDivElement>, item.text)}
                selected={isMenuActive}
                className={item.className}
                sx={{
                  minHeight: 48,
                  justifyContent: 'center',
                  px: LAYOUT.SPACING.sm,
                  py: 1,
                  borderRadius: '24px',
                  mb: 0.5,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.18),
                    },
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Tooltip title={item.text} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: isMenuActive ? theme.palette.primary.main : theme.palette.text.primary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </Tooltip>
              </ListItem>

              <Popover
                open={openPopoverId === item.text}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    mt: 0,
                  },
                }}
              >
                <Paper sx={{ p: 1, minWidth: 180, maxHeight: 300, overflowY: 'auto' }}>
                  {item.isDynamic && isLoadingForms ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <List dense>
                      {item.subItems.map(subItem => {
                        const isSubSelected = location.pathname === subItem.path;
                        return (
                          <ListItem
                            button
                            key={subItem.path}
                            onClick={() => {
                              navigate(subItem.path);
                              handlePopoverClose();
                            }}
                            selected={isSubSelected}
                            className={subItem.className}
                            sx={{
                              borderRadius: '16px',
                              py: 0.75,
                              px: 1.5,
                              transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
                              '&.Mui-selected': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                                },
                              },
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            {subItem.icon && (
                              <ListItemIcon
                                sx={{
                                  minWidth: 28,
                                  color: isSubSelected
                                    ? theme.palette.primary.main
                                    : theme.palette.text.primary,
                                }}
                              >
                                {subItem.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontSize: '0.875rem',
                                fontWeight: isSubSelected ? 500 : 400,
                                color: isSubSelected
                                  ? theme.palette.primary.main
                                  : theme.palette.text.primary,
                                noWrap: true,
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Paper>
              </Popover>
            </React.Fragment>
          );
        }

        return (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={isSelected}
            sx={{
              minHeight: 48,
              justifyContent: isDrawerOpen ? 'initial' : 'center',
              px: LAYOUT.SPACING.sm,
              py: 1,
              borderRadius: '24px',
              mb: 0.5,
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 200ms cubic-bezier(0.2, 0, 0, 1)',
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                },
              },
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
            className={item.className}
          >
            <Tooltip title={!isDrawerOpen ? item.text : ''} placement="right">
              <div>
                <Badge
                  badgeContent={unreadChatLogs}
                  color="error"
                  invisible={!isDrawerOpen && item.text === 'Logs' ? false : true}
                  variant="dot"
                  overlap="circular"
                  max={99}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: isDrawerOpen ? LAYOUT.SPACING.sm : 'auto',
                      justifyContent: 'center',
                      color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </Badge>
              </div>
            </Tooltip>
            {isDrawerOpen && (
              <>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontSize: '0.938rem',
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                  }}
                  sx={{
                    opacity: isDrawerOpen ? 1 : 0,
                  }}
                  className="text-semibold"
                />
                <Badge
                  badgeContent={unreadChatLogs}
                  color="error"
                  invisible={item.text === 'Logs' ? false : true}
                  variant="standard"
                  sx={{ marginRight: 2 }}
                  max={99}
                ></Badge>
              </>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};

export default MenuItems;
